import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ShopCategoryInput,
    ShopGroupFilter,
    ShopGroupInput,
} from '@/lib/api/shop-catalog-api'
import { shopCatalogApi } from '@/lib/api/shop-catalog-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const shopCatalogKeys = {
    categories: ['shop-categories'] as const,
    groups: (f: ShopGroupFilter) => ['shop-groups', f] as const,
    groupsAll: ['shop-groups'] as const,
    group: (id: string) => ['shop-group', id] as const,
}

function toastError(err: unknown, fallback: string) {
    const { title, description } = formatApiError(err, fallback)
    toast.error(title, { description })
}

export function useShopCategories() {
    return useQuery({
        queryKey: shopCatalogKeys.categories,
        queryFn: () => shopCatalogApi.categories(),
    })
}

// Lỗi kiểm dữ liệu (SHOP_*) do màn hiện cạnh ô nhập; hook chỉ báo thành công.
export function useSaveShopCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            input,
        }: {
            id?: string
            input: ShopCategoryInput
        }) =>
            id
                ? shopCatalogApi.updateCategory(id, input)
                : shopCatalogApi.createCategory(input),
        onSuccess: () => {
            toast.success(m.shop_category_saved())
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.categories })
        },
    })
}

export function useDeleteShopCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shopCatalogApi.deleteCategory(id),
        onSuccess: () => {
            toast.success(m.shop_category_deleted())
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.categories })
        },
        onError: (err) => toastError(err, m.shop_category_delete_failed()),
    })
}

export function useReorderShopCategories() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (ids: string[]) => shopCatalogApi.reorderCategories(ids),
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.categories })
        },
        onError: (err) => toastError(err, m.shop_category_reorder_failed()),
    })
}

export function useShopGroups(filter: ShopGroupFilter) {
    return useQuery({
        queryKey: shopCatalogKeys.groups(filter),
        queryFn: () => shopCatalogApi.groups(filter),
    })
}

export function useShopGroup(id: string | undefined) {
    return useQuery({
        queryKey: shopCatalogKeys.group(id ?? ''),
        queryFn: () => shopCatalogApi.group(id ?? ''),
        enabled: !!id,
    })
}

export function useSaveShopGroup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, input }: { id?: string; input: ShopGroupInput }) =>
            id
                ? shopCatalogApi.updateGroup(id, input)
                : shopCatalogApi.createGroup(input),
        onSuccess: (res) => {
            toast.success(m.shop_group_saved())
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.groupsAll })
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.categories })
            if (res.data)
                qc.setQueryData(shopCatalogKeys.group(res.data.id), res)
        },
    })
}

export function useDeleteShopGroup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shopCatalogApi.deleteGroup(id),
        onSuccess: () => {
            toast.success(m.shop_group_deleted())
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.groupsAll })
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.categories })
        },
        onError: (err) => toastError(err, m.shop_group_delete_failed()),
    })
}

export function useSetShopGroupVisibility() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            ...v
        }: {
            id: string
            show_web?: boolean
            show_miniapp?: boolean
        }) => shopCatalogApi.setVisibility(id, v),
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: shopCatalogKeys.groupsAll })
        },
        onError: (err) => toastError(err, m.shop_group_visibility_failed()),
    })
}
